/**
 * Unwrap a proxy response, throwing on failure.
 * @param {Object} result - { success, status, statusText, data, error }
 * @returns {*} result.data on success
 * @throws {Error} on failure
 */
function unwrapProxyResponse(result) {
  if (!result.success) {
    const message =
      result.error || result.data?.detail || result.data?.error || `Proxy request failed with status ${result.status}`
    const error = new Error(message)
    error.status = result.status
    error.data = result.data
    throw error
  }
  return result.data
}

/**
 * Identity service endpoints — routed through the iframe auth proxy.
 * Each operation corresponds to an endpoint on the identity service
 * that the iframe's postMessage handler forwards to.
 */
export const identityServiceEndpoints = {
  LOGIN: async (proxy, username, password) => {
    const result = await proxy.operation('auth:login', {
      body: { username, password, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },

  LOGIN_CODE: async (proxy, code) => {
    const result = await proxy.operation('auth:login-code', {
      body: { code, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },

  LOGIN_TOKEN: async (proxy, token) => {
    const result = await proxy.operation('auth:login-token', {
      body: { token, client_id: IDENTITY_CLIENT_ID },
    })
    return unwrapProxyResponse(result)
  },

  STATUS: async (proxy, currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    const result = await proxy.operation('auth:status', { headers })
    return unwrapProxyResponse(result)
  },

  TOKEN: async (proxy) => {
    const result = await proxy.operation('auth:token')
    return unwrapProxyResponse(result)
  },

  LOGOUT: async (proxy, currentBearerToken) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }
    const result = await proxy.operation('auth:logout', { headers })
    unwrapProxyResponse(result)
  },
}
